import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useShouldShowEmbeddedConfluence } from './hooks/useShouldShowEmbeddedConfluence';
import { type CreateEmbeddedPage, type Cohort } from './types';
import { type BlueprintData } from '../../../state/ui/blueprints/types';
import { type ConfluenceTemplate } from '../../../containers/template/types';
import { fireExposure } from './utils';

type Props = {
	contentId: string | null;
	isConfluenceActivationCompleteForPreselectedTemplate: boolean;
	blueprints: BlueprintData[];
	createEmbeddedPage: CreateEmbeddedPage;
};

export const SelectedTemplatePreview = ({
	contentId,
	blueprints,
	isConfluenceActivationCompleteForPreselectedTemplate,
	createEmbeddedPage,
}: Props): JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const showEmbeddedConfluencePage = useCallback(
		(template: ConfluenceTemplate) => {
			const blueprint = blueprints.find(
				(blueprint) =>
					blueprint.itemModuleCompleteKey === template.blueprintModuleCompleteKey ||
					blueprint.blueprintModuleCompleteKey === template.blueprintModuleCompleteKey,
			);
			if (blueprint) {
				createEmbeddedPage(contentId || undefined, true, blueprint);
				return true;
			}
			return false;
		},
		[contentId, createEmbeddedPage, blueprints],
	);
	const onShouldShowEmbeddedConfluence = useCallback(
		(template: ConfluenceTemplate) => {
			const cohort = FeatureGates.getExperimentValue<Cohort>(
				'project_pages_embedded_confluence_experiment',
				'cohort',
				'not-enrolled',
			);
			const isInVariation = cohort === 'variation';
			fireExposure({
				event: createAnalyticsEvent({
					type: 'sendTrackEvent',
					action: 'viewed',
				}),
				experimentType: 'ab',
				cohort,
			});
			if (isInVariation) {
				return showEmbeddedConfluencePage(template);
			}
			return true;
		},
		[createAnalyticsEvent, showEmbeddedConfluencePage],
	);

	useShouldShowEmbeddedConfluence({
		isConfluenceActivationCompleteForPreselectedTemplate,
		onShouldShowEmbeddedConfluence,
	});

	return null;
};
